import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { TransferModal } from './TransferModal';
import { CloseYearModal } from './CloseYearModal';
import { Modal } from '@/components/common/Modal';
import { TextField, SelectField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { enrollmentsApi } from '@/services/api/endpoints/students';
import { gradeOfferingsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { Enrollment, EnrollmentStatus } from '@/types/domain';
import { formatDate, orDash } from '@/utils/format';

const closeSchema = z.object({
  status: z.enum(['COMPLETED', 'TRANSFERRED', 'WITHDRAWN']),
  completionDate: z.string().min(1, 'Completion date is required'),
  remarks: z.string().max(500),
});

type CloseFormValues = z.infer<typeof closeSchema>;

/**
 * All enrolments across the school.
 *
 * Filtering by section with status ACTIVE gives a class roster, which is the
 * most common reason to open this screen.
 */
export function EnrollmentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('enrollmentDate,desc');

  const [offeringFilter, setOfferingFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<EnrollmentStatus | ''>('');
  const [closing, setClosing] = useState<Enrollment | null>(null);
  const [transferring, setTransferring] = useState<Enrollment | null>(null);
  const [closingYear, setClosingYear] = useState(false);

  const params = {
    ...table.params,
    ...(offeringFilter !== '' ? { gradeOfferingId: offeringFilter } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.enrollments.list(params),
    queryFn: () => enrollmentsApi.list(params),
  });

  /*
   * Filtering is by grade offering, which is already a year-campus-grade
   * combination. Offering a separate year filter would mean the student module
   * resolving years, which belong to the academic module.
   */
  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ enrolmentFilter: true }),
    queryFn: () => gradeOfferingsApi.list({ page: 0, size: 100 }),
    enabled: can(PERMISSIONS.GRADE_OFFERING_VIEW),
  });

  const form = useForm<CloseFormValues>({
    resolver: zodResolver(closeSchema),
    defaultValues: {
      status: 'COMPLETED',
      completionDate: new Date().toISOString().slice(0, 10),
      remarks: '',
    },
  });

  const close = useMutation({
    mutationFn: (values: CloseFormValues) =>
      enrollmentsApi.close(closing?.id ?? '', {
        status: values.status,
        completionDate: values.completionDate,
        remarks: values.remarks === '' ? undefined : values.remarks,
      }),
    onSuccess: () => {
      toast.success('Enrolment closed');
      setClosing(null);
      form.reset();
      void queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const columns: Column<Enrollment>[] = [
    {
      key: 'student',
      header: 'Student',
      render: (row) => (
        <div>
          <div className="font-medium text-ink">{row.studentName}</div>
          <div className="font-mono text-xs text-muted">{row.studentNumber}</div>
        </div>
      ),
    },
    {
      key: 'number',
      header: 'Enrolment no.',
      render: (row) => <span className="font-mono text-xs">{orDash(row.enrollmentNumber)}</span>,
    },
    { key: 'year', header: 'Academic year', render: (row) => orDash(row.academicYearName) },
    { key: 'grade', header: 'Grade', render: (row) => orDash(row.gradeName) },
    { key: 'section', header: 'Section', render: (row) => orDash(row.sectionName) },
    { key: 'roll', header: 'Roll', sortKey: 'rollNumber', render: (row) => orDash(row.rollNumber) },
    {
      key: 'enrolled',
      header: 'Enrolled',
      sortKey: 'enrollmentDate',
      render: (row) => formatDate(row.enrollmentDate),
    },
    { key: 'status', header: 'Status', sortKey: 'status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        row.status === 'ACTIVE' ? (
          <div className="flex justify-end gap-1">
            {can(PERMISSIONS.ENROLLMENT_TRANSFER) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  setTransferring(row);
                }}
              >
                Transfer
              </Button>
            )}
            {can(PERMISSIONS.ENROLLMENT_UPDATE) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  setClosing(row);
                }}
              >
                Close
              </Button>
            )}
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <ListPage
        title="Enrolments"
        description="Student placement by academic year. Closed enrolments are kept as history."
        action={
          can(PERMISSIONS.ENROLLMENT_UPDATE) ? (
            <Button variant="secondary" onClick={() => setClosingYear(true)}>
              Close year
            </Button>
          ) : undefined
        }
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search students…' }}
        filters={
          <>
            <FilterSelect
              label="Class"
              value={offeringFilter}
              onChange={(value) => {
                setOfferingFilter(value);
                table.setPage(0);
              }}
              options={(offerings.data?.data ?? []).map((offering) => ({
                value: offering.id,
                label: `${offering.academicYear.name} · ${offering.grade.name}`,
              }))}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'COMPLETED', label: 'Completed' },
                { value: 'TRANSFERRED', label: 'Transferred' },
                { value: 'WITHDRAWN', label: 'Withdrawn' },
              ]}
            />
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(row) => row.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(row) => navigate(ROUTES.studentDetail(row.studentId))}
          emptyTitle="No enrolments found"
          emptyDescription="Enrol a student from their record to place them in a grade."
        />
      </ListPage>

      <Modal
        open={closing !== null}
        title="Close enrolment"
        description={
          closing === null
            ? undefined
            : `${closing.studentName} — ${closing.gradeName ?? ''} ${closing.academicYearName ?? ''}`
        }
        onClose={() => setClosing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setClosing(null)}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => close.mutate(values))} loading={close.isPending}>
              Close enrolment
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => close.mutate(values))}>
          <p className="rounded-md bg-surface-2 px-3 py-2 text-xs text-muted">
            The record is retained as academic history and its roll number becomes available again.
          </p>
          <SelectField label="Outcome" required error={form.formState.errors.status?.message} {...form.register('status')}>
            <option value="COMPLETED">Completed the year</option>
            <option value="TRANSFERRED">Transferred out</option>
            <option value="WITHDRAWN">Withdrawn</option>
          </SelectField>
          <TextField
            label="Completion date"
            type="date"
            required
            error={form.formState.errors.completionDate?.message}
            {...form.register('completionDate')}
          />
          <TextField label="Remarks" error={form.formState.errors.remarks?.message} {...form.register('remarks')} />
        </form>
      </Modal>

      {closingYear && <CloseYearModal onClose={() => setClosingYear(false)} />}

      {transferring !== null && (
        <TransferModal
          open
          enrollment={transferring}
          onClose={() => setTransferring(null)}
          onTransferred={() => setTransferring(null)}
        />
      )}
    </>
  );
}
